import { NewsApi, NEWS_ELEMENTS_LIMIT, ADMIN_NEWS_ELEMENTS_LIMIT } from '@/api';
import { AddNews, EditNews, News } from '@/models/dashboard';
import { Module } from 'vuex';
import { Actions } from './actions';
import { Mutations } from './mutations';
import { RootState } from './root.store';

export interface NewsState {
  newsList: News[];
  adminPagination: {
    offset: number,
    totalCount: number,
  };
  pagination: {
    offset: number,
    totalCount: number,
  };
  newsToEdit: News;
}

const newsApi = new NewsApi();

export const NewsModule: Module<NewsState, RootState> = {
  state: {
    newsToEdit: { } as News,
    newsList: [],
    pagination: {
      offset: 0,
      totalCount: 0,
    },
    adminPagination: {
      offset: 0,
      totalCount: 0,
    },
  },

  mutations: {
    [Mutations.news.setNews](state, news: News[]) {
      state.newsList = news;
    },
    [Mutations.news.setNewsElementsCount](state, count: number) {
      state.pagination.totalCount = count;
    },
    [Mutations.news.resetPagination](state) {
      state.pagination.offset = 0;
      state.pagination.totalCount = 0;
    },
    [Mutations.news.resetAdminPagination](state) {
      state.adminPagination.offset = 0;
      state.adminPagination.totalCount = 0;
    },
    [Mutations.news.addNewsElements](state, news: News[]) {
      state.newsList.push(...news);
    },
    [Mutations.news.incrementPagination](state) {
      state.pagination.offset += NEWS_ELEMENTS_LIMIT;
    },
    [Mutations.news.incrementAdminPagination](state) {
      state.adminPagination.offset += ADMIN_NEWS_ELEMENTS_LIMIT;
    },
    [Mutations.news.setNewsToEdit](state, news: News) {
      state.newsToEdit = news;
    },
  },

  actions: {
    async [Actions.news.fetchNews]({ commit }): Promise<void> {
      commit(Mutations.news.resetPagination);
      const news = await newsApi.fetchNews();
      commit(Mutations.news.setNews, news.data);
      commit(Mutations.news.setNewsElementsCount, news.totalCount);
    },
    async [Actions.news.fetchAdminNews]({ commit }): Promise<void> {
      const news = await newsApi.fetchNews(0, ADMIN_NEWS_ELEMENTS_LIMIT);
      commit(Mutations.news.setNews, news.data);
      commit(Mutations.news.setNewsElementsCount, news.totalCount);
    },
    async [Actions.news.fetchMoreNews]({ commit, state }): Promise<void> {
      commit(Mutations.news.incrementPagination);
      const news = await newsApi.fetchNews(state.pagination.offset);
      commit(Mutations.news.addNewsElements, news.data);
      commit(Mutations.news.setNewsElementsCount, news.totalCount);
    },
    async [Actions.news.fetchMoreAdminNews]({ commit, state }): Promise<void> {
      commit(Mutations.news.incrementAdminPagination);
      const news = await newsApi.fetchNews(state.adminPagination.offset, ADMIN_NEWS_ELEMENTS_LIMIT);
      commit(Mutations.news.addNewsElements, news.data);
      commit(Mutations.news.setNewsElementsCount, news.totalCount);
    },
    async [Actions.news.deleteNews](_, news: News): Promise<void> {
      await newsApi.deleteNews(news);
    },
    async [Actions.news.fetchNewsById]({ commit }, id: string): Promise<void> {
      const news = await newsApi.fetchNewsById(id);
      commit(Mutations.news.setNewsToEdit, news.data);
    },
    async [Actions.news.addNews](_, news: AddNews): Promise<void> {
      await newsApi.addNews(news);
    },
    async [Actions.news.editNews](_, {id, news}): Promise<void> {
      await newsApi.editNews(id, news);
    },
  },
};
